import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionProps } from '@tiptap/suggestion';

import {
  type MentionItem,
  MentionSuggestion,
  type MentionSuggestionKeyDownHandler,
  type MentionSuggestionRef,
  NO_RESULTS_SENTINEL,
} from './MentionSuggestion';

const MentionWithMarkdown = Mention.extend({
  addStorage() {
    return {
      markdown: {
        serialize(
          state: { write: (text: string) => void },
          node: { attrs: { label?: string; id: string } },
        ) {
          state.write(
            `@[${node.attrs.label ?? node.attrs.id}](${node.attrs.id})`,
          );
        },
        parse: {},
      },
    };
  },
});

export const createMentionExtension = (
  getSuggestions: (query: string) => MentionItem[],
  onOpenChange?: (open: boolean) => void,
  onMentionSearch?: (query: string) => void,
  _getIsLoading?: () => boolean,
  onRegisterUpdate?: (update: () => void) => void,
) => {
  let dismissedQuery: { query: string; from: number } | null = null;

  return MentionWithMarkdown.extend({
    onTransaction: ({ editor }) => {
      if (
        dismissedQuery &&
        (dismissedQuery.from > editor.state.doc.content.size ||
          editor.state.doc.textBetween(
            dismissedQuery.from,
            Math.min(
              dismissedQuery.from + dismissedQuery.query.length + 1,
              editor.state.doc.content.size,
            ),
          ) !== `@${dismissedQuery.query}`)
      ) {
        dismissedQuery = null;
      }
    },
  }).configure({
    HTMLAttributes: {
      class: 'mention',
    },
    renderText: ({ node }) =>
      `@[${node.attrs.label ?? node.attrs.id}](${node.attrs.id})`,
    renderHTML: ({ node, options, suggestion }) => [
      'span',
      options.HTMLAttributes,
      [
        'span',
        { class: 'mentionLabel' },
        `${suggestion?.char ?? '@'}${node.attrs.label ?? node.attrs.id}`,
      ],
    ],
    suggestion: {
      char: '@',
      allowSpaces: true,
      allow: ({ state, range }) => {
        const query = state.doc.textBetween(range.from + 1, range.to);
        if (
          dismissedQuery &&
          (range.from !== dismissedQuery.from ||
            !query.startsWith(dismissedQuery.query))
        ) {
          dismissedQuery = null;
        }
        if (!/\s/.test(query)) return true;
        if (dismissedQuery) return false;
        const [firstSuggestion] = getSuggestions(query);
        if (firstSuggestion?.id === NO_RESULTS_SENTINEL.id) {
          dismissedQuery = { query, from: range.from };
          return false;
        }
        return true;
      },
      items: ({ query }) => getSuggestions(query),
      command: ({ editor, range, props }) => {
        const nodeAfter = editor.view.state.selection.$to.nodeAfter;
        const to = nodeAfter?.text?.startsWith(' ') ? range.to + 1 : range.to;
        editor
          .chain()
          .focus()
          .insertContentAt({ from: range.from, to }, [
            {
              type: 'mention',
              attrs: { ...props, mentionSuggestionChar: '@' },
            },
            { type: 'text', text: ' ' },
          ])
          .setTextSelection(range.from + 2)
          .run();
      },
      render: () => {
        let component: ReactRenderer<MentionSuggestionRef> | null = null;
        let popup: HTMLDivElement | null = null;
        let currentProps: SuggestionProps<MentionItem> | null = null;
        const keyDownRef: {
          current: MentionSuggestionKeyDownHandler | null;
        } = { current: null };

        const positionPopup = (
          clientRect: (() => DOMRect | null) | null | undefined,
        ) => {
          if (!popup || !clientRect) return;
          const rect = clientRect();
          if (!rect) return;
          const viewportWidth =
            document.documentElement.clientWidth || window.innerWidth;
          const popupWidth = popup.getBoundingClientRect().width;
          popup.style.left = `${Math.max(0, Math.min(rect.left, viewportWidth - popupWidth))}px`;
          popup.style.bottom = `${window.innerHeight - rect.top + 4}px`;
          popup.style.top = 'auto';
        };

        return {
          onStart: props => {
            currentProps = props;
            onOpenChange?.(true);
            onMentionSearch?.(props.query);

            component = new ReactRenderer(MentionSuggestion, {
              props: { ...props, keyDownRef },
              editor: props.editor,
            });

            popup = document.createElement('div');
            popup.style.position = 'fixed';
            popup.style.zIndex = '99999';
            popup.appendChild(component.element);
            document.body.appendChild(popup);

            positionPopup(props.clientRect);

            onRegisterUpdate?.(() => {
              if (!currentProps) return;
              const items = getSuggestions(currentProps.query);
              if (
                /\s/.test(currentProps.query) &&
                items[0]?.id === NO_RESULTS_SENTINEL.id
              ) {
                currentProps.editor.view.dispatch(currentProps.editor.state.tr);
                return;
              }
              component?.updateProps({ items });
            });
          },

          onUpdate: props => {
            currentProps = props;
            onMentionSearch?.(props.query);
            component?.updateProps(props);
            positionPopup(props.clientRect);
          },

          onKeyDown: props => {
            if (props.event.key === 'Escape') {
              popup?.remove();
              onOpenChange?.(false);
              return true;
            }
            return (
              keyDownRef.current?.(props) ??
              component?.ref?.onKeyDown(props) ??
              false
            );
          },

          onExit: () => {
            currentProps = null;
            keyDownRef.current = null;
            popup?.remove();
            component?.destroy();
            onOpenChange?.(false);
            onMentionSearch?.('');
          },
        };
      },
    },
  });
};
